<template>
  <div class="workshop-session stack tight">
    <UiPanel tone="work">
      <div class="header">
        <div>
          <h3 class="title">Workshop</h3>
          <div class="subtitle text-muted">Processing and enchanting.</div>
        </div>
        <UiChip v-if="processingAction" variant="active">Active</UiChip>
      </div>

      <div class="controls">
        <UiTabs v-model="tab" :tabs="tabs" tone="work" />
        <div class="text-muted hint" v-if="currentStationLabel">
          {{ ICONS.places.station }} {{ currentStationLabel }}
        </div>
      </div>
    </UiPanel>

    <div v-if="tab === 'processing'">
      <ProcessingWorkbench
        :recipes="recipes"
        :currentAction="processingAction"
        :inventory="inventory"
        :equipment="equipment"
        :currentLocationId="currentLocationId ?? null"
        :skillProgress="skillProgress"
        :skillModifiers="skillModifiers"
        :selectedSkillId="selectedProcessingSkillId"
        :selectedRecipeId="selectedProcessingRecipeId"
        :disabled="disabled"
        @start="(p) => emit('start-processing', p)"
        @pause="emit('pause-processing')"
        @resume="emit('resume-processing')"
        @stop="emit('cancel-processing')"
        @update:selectedSkillId="(value) => emit('update:selectedProcessingSkillId', value)"
        @update:selectedRecipeId="(value) => emit('update:selectedProcessingRecipeId', value)"
      />
    </div>

    <div v-else>
      <EnchantingWorkbench
        :enchantments="enchantments"
        :inventory="inventory"
        :skillProgress="skillProgress"
        :skillModifiers="skillModifiers"
        :currentMana="currentMana"
        :disabled="disabled"
        @apply="(p) => emit('apply-enchantment', p)"
        @remove="(p) => emit('remove-enchantment', p)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type {
  Enchantment,
  InventoryEntry,
  EquipmentSlot,
  ProcessingAction,
  Recipe,
  SkillId,
  SkillProgress
} from "@idle-rpg/shared";
import { ICONS } from "../../../lib/icons";
import EnchantingWorkbench from "./EnchantingWorkbench.vue";
import ProcessingWorkbench from "./ProcessingWorkbench.vue";
import UiChip from "../../../components/ui/UiChip.vue";
import UiPanel from "../../../components/ui/UiPanel.vue";
import UiTabs from "../../../components/ui/UiTabs.vue";

const props = defineProps<{
  recipes: Recipe[];
  processingAction: ProcessingAction | null;
  inventory: InventoryEntry[];
  equipment: Record<EquipmentSlot, InventoryEntry | null>;
  currentLocationId?: string | null;
  enchantments: Enchantment[];
  skillProgress: SkillProgress[];
  skillModifiers?: Partial<Record<SkillId, number>> | null;
  currentMana: number;
  currentLocationName?: string | null;
  currentStationLabel?: string | null;
  selectedProcessingSkillId?: SkillId | null;
  selectedProcessingRecipeId?: string | null;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  (e: "start-processing", payload: { recipeId: string; quantity: number }): void;
  (e: "pause-processing"): void;
  (e: "resume-processing"): void;
  (e: "cancel-processing"): void;
  (e: "apply-enchantment", payload: { instanceId: string; enchantmentId: string }): void;
  (e: "remove-enchantment", payload: { instanceId: string }): void;
  (e: "update:selectedProcessingSkillId", value: SkillId | null): void;
  (e: "update:selectedProcessingRecipeId", value: string | null): void;
}>();

const tab = ref<"processing" | "enchanting">("processing");

const tabs = computed(() => [
  { value: "processing", label: "Processing", icon: ICONS.domains.work },
  { value: "enchanting", label: "Enchanting", icon: ICONS.skills.enchanting }
]);
</script>

<style scoped>
.workshop-session {
  min-width: 0;
}

.header {
  display: flex;
  justify-content: space-between;
  gap: var(--space-md);
  align-items: flex-start;
}

.title {
  margin: 0;
  font-family: var(--font-display);
  letter-spacing: 0.02em;
}

.subtitle {
  font-size: 0.9rem;
  margin-top: 0.15rem;
}

.controls {
  margin-top: var(--space-md);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-md);
  flex-wrap: wrap;
}

.hint {
  font-family: var(--font-mono);
  font-size: 0.85rem;
}
</style>
